// The navigation model behind the header application switcher.
//
// Each application owns a menu. An entry is visible when the session holds its
// `permission`, or any of its `anyPermission` list (OR semantics, e.g.
// authoring tools). Ops, SHIELD and Audience Tracking entries carry a `section`
// so their destinations group under drawer subheaders; Vision Central's menu is
// flat.
//
// A department or schedule entry also carries its `colour` — the drawer paints
// the entry's icon with it, and the destination's own header wears the same
// hue, so the sidebar dot and the page it opens read as one thing.
//
// An application whose pages are about the kiosk fleet declares `portalFilter`,
// and the header offers the global portal selector while it is active. The
// selector writes to one store, and dashboards read it whatever application
// they are rendered in — so an application that renders a dashboard and hides
// the selector narrows its reports by a portal nobody in it can see or clear.

import { createNavigation, ACTIVE_APP_STORAGE_KEY } from '@signifi/vision-shell/navigation';
import { DEPARTMENT_COLOURS } from '@/config/opsFleet';

export const OPS_SECTIONS = ['Daily', 'Planning', 'Solutions', 'Departments', 'Schedules', 'Tools', 'More'];

// SHIELD separates the security work from what governs it: the findings a
// person triages, and the assets and policy that decide what a finding means.
export const SHIELD_SECTIONS = ['Findings', 'Settings'];

// Audience Tracking separates what the cameras measured from the cameras
// themselves: the report pages, and the fleet of enrolled devices whose uploads
// those reports are built from.
export const AUDIENCE_SECTIONS = ['Reports', 'Devices'];

// Which app the header was last showing, so opening the site bare returns to it. The
// shell writes it; every application on the origin reads the same key.
export { ACTIVE_APP_STORAGE_KEY };

// The first app is the default: paths that match no app's `routePrefix` belong
// to it, and it is where a user with no other app lands.
export const APPS = [
  {
    id: 'vision-central',
    title: 'Vision Central',
    icon: 'mdi-monitor-dashboard',
    subtitle: 'Kiosk fleet management',
    portalFilter: true
  },
  {
    id: 'ops',
    title: 'Ops',
    icon: 'mdi-clipboard-text-clock',
    subtitle: 'Project delivery',
    routePrefix: '/ops',
    sections: OPS_SECTIONS,
    // Ops is the app with record-sized menus, so it alone offers the page search
    // (header button and Ctrl+K), and its drawer — three times Vision Central's — is
    // drawn tighter.
    commandPalette: true,
    denseMenu: true
  },
  {
    id: 'shield',
    title: 'Signifi SHIELD',
    icon: 'mdi-shield-check',
    subtitle: 'Security findings',
    routePrefix: '/shield',
    sections: SHIELD_SECTIONS
  },
  {
    id: 'audience',
    title: 'Audience Tracking',
    icon: 'mdi-eye-outline',
    subtitle: 'Camera audience measurement',
    routePrefix: '/audience',
    sections: AUDIENCE_SECTIONS,
    portalFilter: true
  }
];

const visionCentralMenu = [
  { title: 'Locations', value: '/locations', permission: 'kiosks.view', icon: 'mdi-map-marker-circle' },
  { title: 'Kiosks', value: '/kiosks', permission: 'kiosks.view', icon: 'mdi-monitor-multiple' },
  {
    title: 'Reports',
    icon: 'mdi-file-chart',
    permission: 'reports.view',
    children: [
      { title: 'Fleet Uptime', value: '/reports/uptime', permission: 'kiosks.view' },
      { title: 'Vending', value: '/reports/vending', permission: 'transactions.view' }
    ]
  },
  { title: 'Planograms', value: '/planograms', permission: 'planogram.view', icon: 'mdi-view-grid-plus' },
  { title: 'Event Log', value: '/event-log', permission: 'alerts.view', icon: 'mdi-alert-box' },
  { title: 'Users', value: '/users', permission: 'users.view', icon: 'mdi-account-multiple' },
  { title: 'Roles', value: '/roles', permission: 'roles.view', icon: 'mdi-account-lock' },
  { title: 'Scopes', value: '/scopes', permission: 'scopes.view', icon: 'mdi-target-account' },
  { title: 'API Keys', value: '/api-keys', permission: 'apikeys.view', icon: 'mdi-key-chain-variant' },
  { title: 'Proton Workers', value: '/proton-workers', permission: 'proton.view', icon: 'mdi-account-clock' },
  { title: 'Contacts', value: '/contacts', permission: 'contacts.view', icon: 'mdi-card-account-details' },
  { title: 'CONNECT', value: '/connect', permission: 'connect.view', icon: 'mdi-transit-connection-variant' },
  { title: 'Servers', value: '/servers', permission: 'servers.view', icon: 'mdi-server' },
  {
    title: 'DB Admin',
    icon: 'mdi-database-cog',
    anyPermission: ['dbadmin.databases.view', 'dbadmin.audit.view'],
    children: [
      { title: 'Databases', value: '/dbadmin/databases', permission: 'dbadmin.databases.view' },
      { title: 'Imported Backups', value: '/dbadmin/imports', permission: 'dbadmin.databases.view' },
      { title: 'Audit Log', value: '/dbadmin/audit', permission: 'dbadmin.audit.view' }
    ]
  },
  { title: 'Portals', value: '/portals', permission: 'portals.view', icon: 'mdi-web' },
  { title: 'Releases', value: '/releases', permission: 'releases.view', icon: 'mdi-upload' },
  { title: 'Integrations', value: '/integrations', permission: 'projects.view', icon: 'mdi-file-tree' },
  { title: 'Dashboard Builder', value: '/dashboards', anyPermission: ['dashboards.create', 'dashboards.edit', 'dashboards.delete'], icon: 'mdi-pencil-ruler' }
];

const opsMenu = [
  { title: 'Ops Home', value: '/ops', permission: 'ops.view', icon: 'mdi-home-analytics', section: 'Daily' },
  { title: 'Overview', value: '/ops/overview', permission: 'ops.view', icon: 'mdi-view-list-outline', section: 'Daily' },
  { title: 'At a Glance', value: '/ops/glance', permission: 'ops.view', icon: 'mdi-view-dashboard-variant', section: 'Daily' },
  { title: 'This Week', value: '/ops/week', permission: 'ops.view', icon: 'mdi-calendar-week', section: 'Daily' },
  { title: 'Calendar', value: '/ops/calendar', permission: 'ops.view', icon: 'mdi-calendar-month', section: 'Daily' },
  { title: 'Daily Briefing', value: '/ops/briefing', permission: 'ops.view', icon: 'mdi-file-document-outline', section: 'Daily' },
  { title: 'Check-In Board', value: '/ops/checkin-board', permission: 'ops.view', icon: 'mdi-view-column-outline', section: 'Daily' },
  { title: 'Projects', value: '/ops/projects', permission: 'ops.view', icon: 'mdi-folder-multiple', section: 'Daily' },
  { title: 'Pipeline', value: '/ops/pipeline', permission: 'ops.view', icon: 'mdi-view-column', section: 'Planning' },
  { title: 'Readiness', value: '/ops/readiness', permission: 'ops.view', icon: 'mdi-clipboard-check-outline', section: 'Planning' },
  { title: 'Capacity', value: '/ops/capacity', permission: 'ops.view', icon: 'mdi-gauge', section: 'Planning' },
  { title: 'Insights', value: '/ops/insights', permission: 'ops.view', icon: 'mdi-chart-box-outline', section: 'Planning' },
  // Solutions' own surfaces: customer-level work that outlives any one project.
  // Readable by anyone in Ops; written with `ops.solutions.operate` or a project
  // author's key.
  { title: 'Customer Overview', value: '/ops/customers', permission: 'ops.view', icon: 'mdi-domain', section: 'Solutions' },
  { title: 'SA Workload', value: '/ops/solutions/workload', permission: 'ops.view', icon: 'mdi-account-hard-hat-outline', section: 'Solutions' },
  { title: 'In-Life Work', value: '/ops/in-life', permission: 'ops.view', icon: 'mdi-handshake-outline', section: 'Solutions' },
  // Every queue is visible to anyone in Ops and editable only by its own department:
  // a project manager needs to see what the floor is doing without being able to move
  // its cards, and a floor user holds the production key and nothing else.
  { title: 'Check-In', value: '/ops/checkin', permission: 'ops.view', icon: 'mdi-clipboard-check-multiple-outline', section: 'Departments' },
  { title: 'Procurement', value: '/ops/procurement', permission: 'ops.view', icon: 'mdi-cart-outline', section: 'Departments', colour: DEPARTMENT_COLOURS.procurement },
  { title: 'Production', value: '/ops/production', permission: 'ops.view', icon: 'mdi-factory', section: 'Departments', colour: DEPARTMENT_COLOURS.production },
  { title: 'QA', value: '/ops/qa', permission: 'ops.view', icon: 'mdi-clipboard-list-outline', section: 'Departments', colour: DEPARTMENT_COLOURS.qa },
  { title: 'Shipping', value: '/ops/shipping', permission: 'ops.view', icon: 'mdi-truck-outline', section: 'Departments', colour: DEPARTMENT_COLOURS.shipping },
  { title: 'Installation', value: '/ops/installation', permission: 'ops.view', icon: 'mdi-tools', section: 'Departments', colour: DEPARTMENT_COLOURS.installation },
  // UAT is a department's work too, and it is readable by anyone in Ops: answering a
  // checklist takes ops.uat.run, writing the checklists ops.uat.admin.
  { title: 'UAT', value: '/ops/uat', permission: 'ops.view', icon: 'mdi-clipboard-check-outline', section: 'Departments', colour: DEPARTMENT_COLOURS.uat },
  // Three tables of the same shape, so one page over each schedule's own declaration.
  // Visible to anyone in Ops and editable with `ops.schedules.edit`.
  { title: 'Vinyl', value: '/ops/schedules/vinyl', permission: 'ops.view', icon: 'mdi-format-paint', section: 'Schedules', colour: DEPARTMENT_COLOURS.vinyl },
  { title: 'Planogram', value: '/ops/schedules/planogram', permission: 'ops.view', icon: 'mdi-view-grid-outline', section: 'Schedules', colour: DEPARTMENT_COLOURS.planogram },
  { title: 'Software', value: '/ops/schedules/software', permission: 'ops.view', icon: 'mdi-code-braces', section: 'Schedules', colour: DEPARTMENT_COLOURS.software },
  { title: 'Mission Control', value: '/ops/mission-control', permission: 'ops.view', icon: 'mdi-television-play', section: 'Tools' },
  // Readable by anyone in Ops; producing a document takes `ops.documents.generate`.
  { title: 'Documents', value: '/ops/documents', permission: 'ops.view', icon: 'mdi-file-document-multiple-outline', section: 'Tools' },
  { title: 'New Project', value: '/ops/intake', permission: 'ops.projects.create', icon: 'mdi-folder-plus', section: 'Tools' },
  { title: 'Schedule Templates', value: '/ops/templates', permission: 'ops.settings.admin', icon: 'mdi-calendar-multiselect', section: 'More' },
  { title: 'Document Templates', value: '/ops/document-templates', permission: 'ops.settings.admin', icon: 'mdi-file-document-edit-outline', section: 'More' },
  { title: 'Contact Reconciliation', value: '/ops/reconciliation', permission: 'ops.settings.admin', icon: 'mdi-account-convert', section: 'More' },
  // Both are readable by anyone in Ops: the log is how a change is accounted for,
  // and the administered lists are worth reading before asking for one to change.
  { title: 'Activity Log', value: '/ops/activity', permission: 'ops.view', icon: 'mdi-history', section: 'More' },
  { title: 'Ops Settings', value: '/ops/settings', permission: 'ops.view', icon: 'mdi-cog-outline', section: 'More' },
  // The checklists are maintained by the people who hold the key for it; everyone else
  // reaches them from the UAT page, where they read them.
  { title: 'UAT Checklists', value: '/ops/uat/checklists', permission: 'ops.uat.admin', icon: 'mdi-format-list-checks', section: 'More' }
];

// Readable by anyone in SHIELD; the assets and the policy applied to their
// findings are administration, so that entry takes the administration key.
const shieldMenu = [
  { title: 'Findings', value: '/shield/findings', permission: 'shield.view', icon: 'mdi-bug-outline', section: 'Findings' },
  // The board over the worklist rather than in front of it: a triager opens the
  // queue, and posture is the question asked about the queue.
  { title: 'Posture', value: '/shield/posture', permission: 'shield.view', icon: 'mdi-chart-timeline-variant', section: 'Findings' },
  { title: 'Scan runs', value: '/shield/runs', permission: 'shield.view', icon: 'mdi-radar', section: 'Findings' },
  { title: 'Decisions', value: '/shield/decisions', permission: 'shield.view', icon: 'mdi-gavel', section: 'Findings' },
  { title: 'Exceptions', value: '/shield/exceptions', permission: 'shield.view', icon: 'mdi-shield-off-outline', section: 'Findings' },
  { title: 'SHIELD Settings', value: '/shield/settings', permission: 'shield.settings.admin', icon: 'mdi-cog-outline', section: 'Settings' },
  // Grouped with what governs the work rather than with the work, and on its own
  // key: the trail names what everybody did across every domain that writes to
  // it, and the reviewer who reads it often has no business seeing a finding.
  { title: 'Audit Log', value: '/shield/audit', permission: 'shield.audit.view', icon: 'mdi-clipboard-text-search-outline', section: 'Settings' }
];

// Everything an Audience user reads — the two report pages and the state of
// the devices behind them — is on `audience.view`. The other two keys are
// writes exercised elsewhere: `audience.enrol` issues a device's credential
// from the kiosk it is bound to, and `audience.admin` edits the retention and
// threshold settings, so neither opens a destination of its own here.
//
// The two report pages are dashboard documents, so opening one also takes
// `dashboards.view` — what `/api/dashboards/:key` checks — exactly as Fleet
// Uptime does. The grant hands `audience.view` to the roles already holding it,
// so the two travel together until an administrator separates them.
const audienceMenu = [
  { title: 'Audience Overview', value: '/audience/overview', permission: 'audience.view', icon: 'mdi-account-group-outline', section: 'Reports' },
  { title: 'Footfall', value: '/audience/footfall', permission: 'audience.view', icon: 'mdi-walk', section: 'Reports' },
  { title: 'Devices', value: '/audience/devices', permission: 'audience.view', icon: 'mdi-cctv', section: 'Devices' }
];

export const MENUS = {
  'vision-central': visionCentralMenu,
  ops: opsMenu,
  shield: shieldMenu,
  audience: audienceMenu
};

// The functions the header and the router guard read the model through, bound once, so
// the landing page the guard picks and the switcher the shell draws agree on what a
// session can reach.
export const {
  menuFor,
  menuSectionsFor,
  appsFor,
  defaultRouteFor,
  appForPath,
  destinationsFor
} = createNavigation({ apps: APPS, menus: MENUS });
